import RelatedContentHeader from './RelatedContentHeader';
import { relatedContentSubHeader } from './RelatedContentHeader.locstring.json';
import { FocusZone, FocusZoneTabbableElements } from '@fluentui/react/lib/FocusZone';
import { KeyCodes } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react-lite';
import { ReadingPaneEntrySourceType } from 'owa-calendar-forms-common/lib/schema/ReadingPaneEntrySourceType';
import { RelatedEmailsWell, RelatedFilesWell } from 'owa-calendar-related-content';
import ColorScheme from 'owa-color-utils/lib/ColorScheme';
import { getOptimalTextColorString } from 'owa-color-utils/lib/colorUtils';
import loc from 'owa-localize';
import { getOpxHostApi } from 'owa-opx';
import { isHostAppFeatureEnabled } from 'owa-hostapp-feature-flags';
import { useClientStyles } from 'owa-react-hooks/lib/useClientStyles';
import { getPaletteAsRawColors } from 'owa-theme';
import * as React from 'react';
import {
    getRelatedEmails,
    getRelatedFiles,
    getInsightsTraceId,
    InsightsRenderLoggingHelper,
} from 'owa-meeting-insights';
import { InsightsDataFetchSource } from 'owa-meeting-insights-types-and-flights';

const styles = {
    base: require('./RelatedContentInsights.scss'),
    desktop: require('./RelatedContentInsights.outlookdesktop.scss'),
};
const classNames = require('classnames');

export interface RelatedContentInsightsProps {
    meetingId: string;
    colorScheme: ColorScheme;
    readingPaneEntrySource?: ReadingPaneEntrySourceType; // null when rendering from CITI card
    insightsEntrySource: InsightsDataFetchSource;
    className?: string;
    hideInsightsHeader?: boolean;
}

export default observer(function RelatedContentInsights(props: RelatedContentInsightsProps) {
    const { meetingId, insightsEntrySource, colorScheme } = props;

    const { getClass } = useClientStyles(styles);

    const insightsContainerRef = React.useRef<HTMLElement>();
    const cachedInsightsTraceId = getInsightsTraceId(meetingId, insightsEntrySource);

    const filesCount = getRelatedFiles(meetingId, insightsEntrySource).length;
    const emailCount = getRelatedEmails(meetingId, insightsEntrySource).length;

    // Display insight container background and header only if insights from files/emails exist
    const haveInsightsToDisplay = filesCount > 0 || emailCount > 0;

    React.useEffect(() => {
        // Notify OPX scenario about the meeting insights container height
        if (
            isHostAppFeatureEnabled('insightsCountUpdatedHandler') &&
            haveInsightsToDisplay &&
            insightsContainerRef.current
        ) {
            getOpxHostApi().onMeetingInsightsContainerHeightUpdated(
                insightsContainerRef.current.getBoundingClientRect().height
            );
        }
    }, [cachedInsightsTraceId]);

    const insightsContainerRefFunction = (element: HTMLElement) => {
        insightsContainerRef.current = element;
    };

    const textColor = getOptimalTextColorString(
        getPaletteAsRawColors().neutralLighterAlt,
        colorScheme,
        colorScheme.calDarkAlt
    );

    const focusZoneContainerRef = React.useRef<HTMLDivElement>();
    const handleFocusZoneContainerKeyDown = React.useCallback((evt: React.KeyboardEvent) => {
        switch (evt.keyCode) {
            // need to handle ESC key to allow user to escape focus zone,
            // as this does not seem to be default-handled by Fluent UI component
            case KeyCodes.escape:
                focusZoneContainerRef.current?.focus();
                evt.stopPropagation();
                evt.preventDefault();
                break;
        }
    }, []);

    const renderRelatedContent = (
        filesCount: number,
        emailCount: number,
        textColor: string
    ): JSX.Element => {
        if (filesCount + emailCount < 1) {
            return <></>;
        }

        return (
            <FocusZone
                isCircularNavigation={isHostAppFeatureEnabled('focusZoneCicularNavigation')}
                handleTabKey={FocusZoneTabbableElements.all}>
                {filesCount > 0 && (
                    <RelatedFilesWell
                        color={textColor}
                        colorScheme={props.colorScheme}
                        insightsEntrySource={props.insightsEntrySource}
                        meetingId={props.meetingId}
                        readingPaneEntrySource={props.readingPaneEntrySource}
                    />
                )}
                {emailCount > 0 && (
                    <RelatedEmailsWell
                        adjustTopMargin={filesCount > 0}
                        color={textColor}
                        colorScheme={props.colorScheme}
                        insightsEntrySource={props.insightsEntrySource}
                        meetingId={props.meetingId}
                        readingPaneEntrySource={props.readingPaneEntrySource}
                    />
                )}
            </FocusZone>
        );
    };

    return (
        <>
            <InsightsRenderLoggingHelper
                eventId={meetingId}
                source={insightsEntrySource}
                hasInsightsToShow={haveInsightsToDisplay}
            />
            <div
                className={
                    haveInsightsToDisplay
                        ? classNames(getClass('insightsContainer'), props.className)
                        : ''
                }
                ref={insightsContainerRefFunction}
                role="region"
                aria-label={loc(relatedContentSubHeader)}>
                {haveInsightsToDisplay && (
                    <RelatedContentHeader color={textColor} hideHeader={props.hideInsightsHeader} />
                )}
                <div
                    className={getClass('insightsContent')}
                    ref={focusZoneContainerRef}
                    tabIndex={-1}
                    onKeyDown={
                        isHostAppFeatureEnabled('focusZoneCicularNavigation')
                            ? handleFocusZoneContainerKeyDown
                            : undefined
                    }>
                    {renderRelatedContent(filesCount, emailCount, textColor)}
                </div>
            </div>
        </>
    );
});
